(() => {
    "use strict";

    const PREFIX = "tlc-";
    const P = { INSTANCE: "_tl_instance_id", CAMPAIGN: "_tl_campaign_id", TYPE: "_tl_item_type", GROUP: "_tl_group_index" };
    const CACHE_TTL = 10 * 60 * 1000;
    const state = { rootUrl: "/", promises: {}, cartModeWidgets: new Set(), campaigns: null };

    // ---------- Shared helpers ----------

    const rootPath = (path) => (state.rootUrl + path).replace(/\/+/g, "/");
    const sumQty = (lines) => lines.reduce((sum, line) => sum + line.quantity, 0);
    const maxQty = (group) => (parseInt(group.maxQty, 10) > 0 ? parseInt(group.maxQty, 10) : Infinity);
    const requirementOf = (group) => group.purchaseRequirementType || (group.requiredQty ? "min_quantity" : "none");
    const lineKey = (campaign) => campaign.id + ":mnm";
    const ownsLine = (campaign, props) => [lineKey(campaign), String(campaign.id)].includes(String(props[P.CAMPAIGN]));
    const bundleProps = (instanceId, campaign, groupIndex) => ({
        [P.INSTANCE]: instanceId,
        [P.CAMPAIGN]: lineKey(campaign),
        [P.TYPE]: "condition",
        [P.GROUP]: groupIndex,
    });

    function lowestRewardQty(campaign) {
        const values = (campaign.rewards || []).map((r) => Number(r.conditionValue) || 0);
        return values.length ? Math.min(...values) : 0;
    }

    // Promise cached by key; a failure is dropped so the next call retries.
    function once(key, load) {
        state.promises[key] =
            state.promises[key] ||
            load().catch((error) => {
                delete state.promises[key];
                throw error;
            });
        return state.promises[key];
    }

    async function getJson(path) {
        const res = await fetch(rootPath(path), { headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(path + " " + res.status);
        return res.json();
    }

    function injectStyle(id, css) {
        if (document.getElementById(id)) return;
        const style = document.createElement("style");
        style.id = id;
        style.textContent = css;
        document.head.appendChild(style);
    }

    // ---------- 1. Cart adoption ----------

    // Product ids of a collection (250 per page), kept in sessionStorage for 10 minutes.
    const collectionProductIds = (handle) =>
        once("col:" + handle, async () => {
            const key = "tl_custom_col_" + handle;
            try {
                const cached = JSON.parse(sessionStorage.getItem(key));
                if (cached && Date.now() - cached.at < CACHE_TTL) return cached.ids;
            } catch (error) {}

            const ids = [];
            for (let page = 1; page <= 20; page++) {
                const products = (await getJson(`/collections/${handle}/products.json?limit=250&page=${page}`)).products || [];
                ids.push(...products.map((p) => String(p.id)));
                if (products.length < 250) break;
            }
            try {
                sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), ids }));
            } catch (error) {}
            return ids;
        });

    // fn(line) => whether the cart line belongs to the campaign's group.
    const matcherFor = (campaign) =>
        once("match:" + campaign.id, async () => {
            const group = campaign.mixItemGroups[0];
            const items = group.items || [];
            if (group.type === "collection") {
                const ids = new Set((await Promise.all(items.map((c) => collectionProductIds(c.handle)))).flat());
                return (line) => ids.has(String(line.product_id));
            }
            const byProduct = Object.fromEntries(items.map((p) => [String(p.id), (p.variants || []).map((v) => String(v.id))]));
            return (line) => {
                const variants = byProduct[String(line.product_id)];
                return !!variants && (!variants.length || variants.includes(String(line.variant_id)));
            };
        });

    // Only single-group, min-quantity mixnmatch campaigns with a code (no required products).
    function isAdoptable(campaign) {
        const groups = (campaign && campaign.mixItemGroups) || [];
        return (
            !!campaign &&
            campaign.type === "mixnmatch" &&
            groups.length === 1 &&
            requirementOf(groups[0]) === "min_quantity" &&
            !groups[0].requiredProductsEnabled &&
            (campaign.rewards || []).length > 0 &&
            !!campaign.discountCode
        );
    }

    // `owned`: lines already tagged for the campaign; `loose`: plain paid lines that match it.
    function campaignLines(cart, campaign, matches) {
        const owned = [];
        const loose = [];
        cart.items.forEach((line) => {
            const props = line.properties || {};
            if (props[P.CAMPAIGN]) {
                if (ownsLine(campaign, props) && props[P.TYPE] === "condition") owned.push(line);
            } else if (line.quantity > 0 && line.final_price > 0 && matches(line)) {
                loose.push(line);
            }
        });
        return { owned, loose };
    }

    const sellingPlanOf = (line) => line.selling_plan_allocation?.selling_plan?.id || null;
    const distinctProducts = (lines) => new Set(lines.map((line) => String(line.product_id))).size;
    const newInstanceId = () => PREFIX + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    const baseProperties = (line) =>
        Object.fromEntries(
            Object.entries(line.properties || {}).filter(([k, v]) => !k.startsWith("_tl_") && v !== null && v !== undefined && v !== ""),
        );

    // Shopify merges lines by variant + selling plan + properties, so this identifies a line.
    const identity = (variantId, sellingPlan, props) =>
        `${variantId}|${sellingPlan || ""}|${JSON.stringify(Object.keys(props).sort().map((k) => [k, String(props[k])]))}`;

    // Consecutive bundles of `size` units; the last one may be smaller but not below `threshold`.
    function splitInOrder(units, size, threshold) {
        const bundles = [];
        let cursor = 0;
        while (units.length - cursor >= threshold) {
            const take = Math.min(size, units.length - cursor);
            bundles.push(units.slice(cursor, cursor + take));
            cursor += take;
        }
        return { bundles, rest: units.slice(cursor) };
    }

    // Unique groups: one unit per product; products with the most units first, so the most bundles form.
    function splitUnique(units, size, threshold) {
        const byProduct = {};
        units.forEach((unit) => (byProduct[unit.productId] = byProduct[unit.productId] || []).push(unit));
        const queues = Object.values(byProduct);
        const bundles = [];
        for (;;) {
            const picked = queues.filter((q) => q.length).sort((a, b) => b.length - a.length).slice(0, size);
            if (picked.length < threshold) break;
            bundles.push(picked.map((q) => q.shift()));
        }
        return { bundles, rest: queues.flat() };
    }

    // Cart changes for one campaign, or null when the cart is stable. Bundle size = group maxQty;
    // full "tlc-" bundles are kept, the rest regrouped (unique: only invalid bundles are regrouped).
    function planCampaign(cart, campaign, matches) {
        const group = campaign.mixItemGroups[0];
        const unique = !!group.unique;
        const size = maxQty(group);
        const threshold = Math.max(parseInt(group.minQty, 10) || 1, lowestRewardQty(campaign), 1);
        if (threshold > size) return null;

        const { owned, loose } = campaignLines(cart, campaign, matches);
        const instances = {};
        owned.forEach((line) => {
            const id = String(line.properties[P.INSTANCE] || "");
            if (id.startsWith(PREFIX)) (instances[id] = instances[id] || []).push(line);
        });

        const partial = [];
        const invalid = [];
        Object.entries(instances).forEach(([id, lines]) => {
            const qty = sumQty(lines);
            const repeats = unique && (distinctProducts(lines) !== lines.length || lines.some((l) => l.quantity > 1));
            if (repeats || qty > size || qty < threshold) invalid.push(id);
            else if (qty < size) partial.push(id);
        });

        const looseUnits = sumQty(loose);
        const stable = unique
            ? !invalid.length && distinctProducts(loose) < threshold
            : !invalid.length && partial.length <= 1 && (partial.length ? looseUnits === 0 : looseUnits < threshold);
        if (stable) return null;

        const pool = loose.concat(...(unique ? invalid : partial.concat(invalid)).map((id) => instances[id]));
        const units = pool.flatMap((line) =>
            Array.from({ length: line.quantity }, () => ({
                productId: String(line.product_id),
                variantId: line.variant_id,
                sellingPlan: sellingPlanOf(line),
                base: baseProperties(line),
            })),
        );

        const targets = {};
        const addTarget = (unit, properties) => {
            const id = identity(unit.variantId, unit.sellingPlan, properties);
            targets[id] = targets[id] || { variantId: unit.variantId, sellingPlan: unit.sellingPlan, properties, qty: 0 };
            targets[id].qty += 1;
        };
        const split = (unique ? splitUnique : splitInOrder)(units, size, threshold);
        split.bundles.forEach((bundle) => {
            const props = bundleProps(newInstanceId(), campaign, 0);
            bundle.forEach((unit) => addTarget(unit, Object.assign({}, unit.base, props)));
        });
        split.rest.forEach((unit) => addTarget(unit, unit.base));

        const current = {};
        pool.forEach((line) => {
            const id = identity(line.variant_id, sellingPlanOf(line), line.properties || {});
            current[id] = current[id] || { key: line.key, qty: 0 };
            current[id].qty += line.quantity;
        });

        const updates = {};
        Object.entries(current).forEach(([id, line]) => {
            const qty = targets[id] ? targets[id].qty : 0;
            if (qty !== line.qty) updates[line.key] = qty;
        });
        const additions = Object.entries(targets)
            .filter(([id]) => !current[id])
            .map(([, t]) => ({ id: t.variantId, quantity: t.qty, properties: t.properties, ...(t.sellingPlan && { selling_plan: t.sellingPlan }) }));

        return additions.length || Object.keys(updates).length ? { additions, updates } : null;
    }

    // ---------- 2. Cart mode ----------

    const CART_MODE_CLASS = "tl-custom-cart-mode";
    const CM = "tl-mixnmatch." + CART_MODE_CLASS;
    // The app writes campaign colors inline on <tl-mixnmatch>, so !important is needed.
    const CART_MODE_CSS =
        `${CM} .tl-mixnmatch__group-container,${CM} .tl-mixnmatch__divider,${CM} .tl-mixnmatch__footer{display:none!important}` +
        `${CM}{--tl-color-bg-secondary:#F7F0E2!important;--tl-color-text:#3B3025!important;--tl-color-text-secondary:#3B3025!important;` +
        "--tl-color-brand:#D1A551!important;--tl-gradient-brand:none!important;" +
        // track + message strip tinted so they stay visible on the box
        "--tl-color-brand-secondary:color-mix(in srgb,#D1A551 22%,#F7F0E2)!important;" +
        "--tl-color-bg:color-mix(in srgb,#D1A551 12%,#F7F0E2)!important;" +
        "--tl-color-border:color-mix(in srgb,#D1A551 30%,#F7F0E2)!important}";

    function isCartMode(context, campaign) {
        if (!isAdoptable(campaign) || !String(context?.template || "").startsWith("collection")) return false;
        const group = campaign.mixItemGroups[0];
        const handle = decodeURIComponent((location.pathname.match(/\/collections\/([^/?#]+)/) || [])[1] || "");
        return group.type === "collection" && !!handle && (group.items || []).some((c) => c.handle === handle);
    }

    // Fake widget selections from the cart: units toward the current bundle (4 in cart, size 3 -> 1; 3 -> 3).
    function cartSelections(cart, campaign, matches) {
        const size = maxQty(campaign.mixItemGroups[0]);
        const { owned, loose } = campaignLines(cart, campaign, matches);
        const lines = loose.concat(owned);
        const total = sumQty(lines);
        let remaining = total === 0 ? 0 : size === Infinity ? total : ((total - 1) % size) + 1;
        const items = [];
        lines.forEach((line) => {
            if (remaining <= 0) return;
            const quantity = Math.min(line.quantity, remaining);
            remaining -= quantity;
            items.push({ productId: String(line.product_id), variantId: String(line.variant_id), price: line.original_price / 100, quantity });
        });
        return { 0: items };
    }

    // Swap the empty message on a completed bundle for a hint about the next set.
    function enableCartMode(widget) {
        if (widget.__tlCartMode) return;
        widget.__tlCartMode = true;
        widget.classList.add(CART_MODE_CLASS);

        const original = widget._renderUpsellMessage;
        widget._renderUpsellMessage = function () {
            let message = original.call(this);
            if (typeof message === "string") message = message.replace(/ \(Set 1\)/, "");
            const size = maxQty(this.campaign.mixItemGroups[0]);
            if (message || size === Infinity || this._totalSelectedQuantity < size) return message;
            return `Bundle unlocked! Add ${size} more to get another set.`;
        };
    }

    async function syncCartMode(sdk) {
        state.cartModeWidgets.forEach((w) => w.isConnected || state.cartModeWidgets.delete(w));
        if (!state.cartModeWidgets.size) return;

        const cart = await sdk.ShopifyAPI.getCart();
        for (const widget of state.cartModeWidgets) {
            try {
                widget._selections = cartSelections(cart, widget.campaign, await matcherFor(widget.campaign));
            } catch (error) {
                console.error("[custom] cart mode sync failed:", error);
            }
        }
    }

    // ---------- 3. Volume Discount ----------

    const BUY_BUTTONS_SELECTOR = "span.buy-buttons-block";
    const PRODUCT_FORM_SELECTOR = 'form[action*="/cart/add"]';
    const VD_CSS =
        PRODUCT_FORM_SELECTOR + " + tl-volume-discount .tl-vd__header{margin-top:12px}" +
        // Long tier badges wrap instead of running under the price (app CSS has nowrap).
        "tl-volume-discount .tl-vd__tier-label-row .tl-vd__tier-badge{white-space:normal;flex-shrink:1;min-width:0;max-width:100%}" +
        "tl-volume-discount .tl-vd__tier-card[data-tl-ribbon]{padding-top:max(var(--tl-space-400),26px)}";

    // Ribbon per tier (campaign id -> tier id -> text); the app only ribbons the default tier.
    const TIER_RIBBONS = { 76: { 1: "Popular", 2: "Best Value" } };
    const RIBBON_RE = /<span class="tl-vd__best-ribbon">[\s\S]*?<\/span>/;

    function patchTierRibbons() {
        customElements.whenDefined("tl-volume-discount").then(() => {
            const proto = customElements.get("tl-volume-discount").prototype;
            const original = proto._renderTierCard;
            if (typeof original !== "function" || original.__tlPatched) return;

            proto._renderTierCard = function (tier) {
                let html = original.apply(this, arguments);
                const ribbons = TIER_RIBBONS[this.campaign?.id];
                if (!ribbons || !tier) return html;

                html = html.replace(RIBBON_RE, "");
                if (!ribbons[tier.id]) return html;
                const ribbon = document.createElement("span");
                ribbon.className = "tl-vd__best-ribbon";
                ribbon.textContent = ribbons[tier.id];
                return html.replace(/(data-tier-id="[^"]*")/, "$1 data-tl-ribbon").replace(/>/, ">" + ribbon.outerHTML);
            };
            proto._renderTierCard.__tlPatched = true;
        });
    }

    // The app inserts the skeleton above Add to cart; move it under the form as soon as it is
    // inserted (the widget replaces it in place, so no jump). Hooked on requestUpdate: lifecycle
    // callbacks are captured at define time, so patching connectedCallback has no effect.
    function placeVolumeDiscountBelowAddToCart() {
        const Skeleton = customElements.get("tl-volume-discount-skeleton");
        const original = Skeleton && Skeleton.prototype.requestUpdate;
        if (typeof original !== "function" || original.__tlPatched) return;
        injectStyle("tl-custom-vd-style", VD_CSS);

        Skeleton.prototype.requestUpdate = function () {
            if (this.isConnected && !this.__tlPlaced) {
                this.__tlPlaced = true;
                try {
                    // Keep app blocks / embeds where the merchant put them.
                    if (!this.closest(".tl-mixnmatch-block, [data-campaign-id]")) {
                        let block = this.nextElementSibling;
                        if (!block || !block.matches(BUY_BUTTONS_SELECTOR)) block = document.querySelector(BUY_BUTTONS_SELECTOR);
                        const anchor = block && (block.querySelector(PRODUCT_FORM_SELECTOR) || block);
                        if (anchor && anchor.nextElementSibling !== this) anchor.insertAdjacentElement("afterend", this);
                    }
                } catch (error) {
                    console.error("[custom] move volume discount failed:", error);
                }
            }
            return original.apply(this, arguments);
        };
        Skeleton.prototype.requestUpdate.__tlPatched = true;
    }

    // ---------- 4. Fixed Bundle ----------

    // Vessel's column flex shrinks the host (container-type:inline-size) to 0px wide. Campaign 11 has
    // both display modes off, yet the app still mounts a skeleton and leaves an empty host whose
    // margins push Add to cart down: hide both. "body" outweighs app.css, which loads later.
    const FB_CSS =
        "tl-fixed-bundle,tl-fixed-bundle-skeleton{width:100%}" +
        "body tl-fixed-bundle-skeleton,tl-fixed-bundle:has(> .tl-fb__empty, > tl-fixed-bundle-skeleton){display:none}";

    // ---------- 5. Campaign banner (sections/tl-bundle-banner.liquid) ----------
    // The section holds the look; the campaign in data-campaign-id gives products, quantities,
    // prices and the discount. Adds with the widget's line properties, one cart line per product.

    const BANNER_ROWS = "[data-tl-rows]";
    const productJson = (handle) => once("product:" + handle, () => getJson(`/products/${encodeURIComponent(handle)}.js`));

    // One line per product of each group: the group minimum is spread over its products (at least
    // 1 each); unique groups take 1 of each product, up to the group max.
    function bannerLines(campaign) {
        const groups = campaign.mixItemGroups || [];
        const lines = [];
        groups.forEach((group, groupIndex) => {
            if (group.type !== "product") throw new Error(`group ${groupIndex} is not a product group`);

            let items = (group.items || []).filter((item) => item && item.handle);
            let need = requirementOf(group) === "min_quantity" ? parseInt(group.minQty, 10) || 0 : 0;
            if (groups.length === 1) need = Math.max(need, lowestRewardQty(campaign));
            if (group.unique) items = items.slice(0, maxQty(group));

            items.forEach((item, i) => {
                const quantity = group.unique ? 1 : Math.max(1, Math.floor(need / items.length) + (i < need % items.length ? 1 : 0));
                lines.push({ item, groupIndex, quantity });
            });
        });
        if (!lines.length) throw new Error("campaign has no products");
        return lines;
    }

    // First available variant among those picked in the campaign (all variants when none picked).
    function pickVariant(product, item) {
        const allowed = (item.variants || []).map((v) => String(v.id).split("/").pop());
        const variants = (product.variants || []).filter((v) => !allowed.length || allowed.includes(String(v.id)));
        return variants.find((v) => v.available) || variants[0] || null;
    }

    // Highest reward reached by `units` (quantity tiers).
    const reachedReward = (campaign, units) =>
        (campaign.rewards || [])
            .filter((r) => (Number(r.conditionValue) || 0) <= units)
            .sort((a, b) => Number(b.conditionValue) - Number(a.conditionValue))[0];

    // Same formula as the widget's total (percentage with cap, amount off, fixed bundle price).
    function bundleSaving(reward, total, sdk) {
        const value = Number(reward && reward.discountValue);
        if (!value) return 0;
        const convert = (amount) => sdk.convertPrice(amount, sdk.Runtime.get("exchangeRate"));

        if (reward.discountType === "percentage") {
            const cap = Number(reward.discountMaxAmount) > 0 ? convert(Number(reward.discountMaxAmount)) : Infinity;
            return Math.min((total * value) / 100, cap);
        }
        if (reward.discountType === "amount") return Math.min(total, convert(value));
        if (reward.discountType === "fixed") return Math.max(0, total - convert(value));
        return 0;
    }

    // data-tl-* bindings: text (+ truncate / trim-zero-cents), title, template, href, src, show.
    const BINDINGS = {
        "data-tl-text": (el, key, val) => {
            let text = val(key);
            if (el.hasAttribute("data-tl-trim-zero-cents")) text = text.replace(/[.,]00(?![\d.,])/, "");
            const max = parseInt(el.getAttribute("data-tl-truncate"), 10);
            el.textContent = max > 3 && text.length > max ? text.slice(0, max - 3) + "..." : text;
        },
        "data-tl-title": (el, key, val) => el.setAttribute("title", val(key)),
        "data-tl-template": (el, template, val, values) =>
            (el.textContent = template.replace(/\{(\w+)\}/g, (match, key) => (key in values ? val(key) : match))),
        "data-tl-href": (el, key, val) => (val(key) ? el.setAttribute("href", val(key)) : el.removeAttribute("href")),
        "data-tl-src": (el, key, val) => (val(key) ? el.setAttribute("src", val(key)) : el.removeAttribute("src")),
        "data-tl-show": (el, key, val) => (el.hidden = !val(key)),
    };

    // Fill bindings under scope; `skip` leaves a subtree (the rows) alone.
    function bindValues(scope, values, skip) {
        const val = (key) => (values[key] === undefined || values[key] === null ? "" : String(values[key]));
        Object.entries(BINDINGS).forEach(([attr, apply]) =>
            scope.querySelectorAll(`[${attr}]`).forEach((el) => {
                if (!skip || !el.closest(skip)) apply(el, el.getAttribute(attr), val, values);
            }),
        );
    }

    function setBannerMessage(root, text) {
        const message = root.querySelector("[data-tl-message]");
        if (message) message.textContent = text || "";
    }

    // Unusable campaign: customers do not see the banner; the theme editor shows why.
    function hideBanner(root, reason) {
        root.setAttribute("data-tl-state", "unavailable");
        if (window.Shopify?.designMode) {
            const rows = root.querySelector(BANNER_ROWS);
            if (rows) rows.hidden = true;
            return setBannerMessage(root, `${root.getAttribute("data-text-unavailable") || ""} (${reason})`);
        }
        (root.closest(".shopify-section") || root).style.display = "none";
    }

    async function renderBanner(root, sdk) {
        if (root.__tlBanner) return;
        root.__tlBanner = true;

        const campaignId = String(root.getAttribute("data-campaign-id") || "").trim();
        const campaign = (state.campaigns || []).find((c) => c.type === "mixnmatch" && String(c.id) === campaignId);
        if (!campaign) {
            console.warn("[custom] banner: no active Mix & Match campaign " + JSON.stringify(campaignId));
            return hideBanner(root, `campaign ${JSON.stringify(campaignId)} is not an active Mix & Match campaign`);
        }

        try {
            const lines = bannerLines(campaign);
            const products = await Promise.all(lines.map((line) => productJson(line.item.handle)));
            const rows = lines.map((line, i) => {
                const variant = pickVariant(products[i], line.item);
                if (!variant) throw new Error(`no variant of ${line.item.handle} in the campaign`);
                return { line, product: products[i], variant };
            });

            const format = (amount) => sdk.formatMoney(amount, sdk.Runtime.get("moneyFormat"));
            const template = root.querySelector("template[data-tl-row]");
            const list = root.querySelector(BANNER_ROWS);
            let total = 0;
            let units = 0;

            list.replaceChildren(
                ...rows.map(({ line, product, variant }, i) => {
                    const price = variant.price / 100;
                    total += price * line.quantity;
                    units += line.quantity;
                    const node = template.content.cloneNode(true);
                    bindValues(node, {
                        index: String(i + 1).padStart(2, "0"),
                        title: product.title,
                        variant_title: variant.title === "Default Title" ? "" : variant.title,
                        url: rootPath("/products/" + product.handle) + "?variant=" + variant.id,
                        image: ((src) => (src ? src + (src.includes("?") ? "&" : "?") + "width=200" : ""))(
                            variant.featured_image?.src || product.featured_image,
                        ),
                        price: format(price),
                        compare_price: variant.compare_at_price > variant.price ? format(variant.compare_at_price / 100) : "",
                        quantity: line.quantity,
                        line_price: format(price * line.quantity),
                    });
                    return node;
                }),
            );
            list.hidden = false;

            const saving = bundleSaving(reachedReward(campaign, units), total, sdk);
            const hasSaving = saving >= 0.01;
            bindValues(
                root,
                {
                    total_price: format(total),
                    final_price: format(total - saving),
                    savings: hasSaving ? format(saving) : "",
                    savings_percent: hasSaving ? Math.round((saving / total) * 100) + "%" : "",
                    quantity: units,
                },
                BANNER_ROWS,
            );

            const button = root.querySelector("[data-tl-add]");
            if (!button) return;
            if (rows.some((row) => !row.variant.available)) {
                const label = root.querySelector("[data-tl-label]");
                if (label && root.getAttribute("data-text-sold-out")) label.textContent = root.getAttribute("data-text-sold-out");
                button.disabled = true;
                return root.setAttribute("data-tl-state", "sold-out");
            }
            button.disabled = false;
            button.addEventListener("click", () => addBanner(root, campaign, rows, sdk));
            root.setAttribute("data-tl-state", "ready");
        } catch (error) {
            console.error(`[custom] banner ${campaignId} failed:`, error);
            hideBanner(root, error.message);
        }
    }

    async function addBanner(root, campaign, rows, sdk) {
        const button = root.querySelector("[data-tl-add]");
        if (button.getAttribute("aria-busy") === "true") return;
        button.setAttribute("aria-busy", "true");
        setBannerMessage(root, "");

        const instanceId = "tl_mixnmatch_" + sdk.generateUID();
        const items = rows.map(({ line, variant }) => ({
            id: variant.id,
            quantity: line.quantity,
            properties: bundleProps(instanceId, campaign, line.groupIndex),
        }));

        try {
            await sdk.ShopifyAPI.addItems(items);
            // CartMonitor ignores app-originated cart requests, so apply the code here.
            const codes = ((await sdk.ShopifyAPI.getCart()).discount_codes || []).map((d) => d.code);
            if (campaign.discountCode && !codes.includes(campaign.discountCode)) {
                await sdk.ShopifyAPI.applyDiscountCodes(codes.concat(campaign.discountCode));
            }
            await sdk.ThemeAdapter.refreshCart();
            window.dispatchEvent(new CustomEvent("cart:refresh"));
        } catch (error) {
            console.error("[custom] banner add to cart failed:", error);
            // Shopify may add part of the bundle on a stock error, so remove this instance.
            if (error?.status === 422) {
                try {
                    const cart = await sdk.ShopifyAPI.getCart();
                    const updates = Object.fromEntries(
                        cart.items.filter((item) => item.properties?.[P.INSTANCE] === instanceId).map((item) => [item.key, 0]),
                    );
                    if (Object.keys(updates).length) await sdk.ShopifyAPI.updateCart(updates);
                    await sdk.ThemeAdapter.refreshCart();
                } catch (rollbackError) {
                    console.error("[custom] banner rollback failed:", rollbackError);
                }
            }
            setBannerMessage(root, root.getAttribute("data-text-error"));
        } finally {
            button.removeAttribute("aria-busy");
        }
    }

    function renderBanners(sdk, scope) {
        if (state.campaigns) (scope || document).querySelectorAll("[data-tl-campaign-banner]").forEach((root) => renderBanner(root, sdk));
    }

    // ---------- Hooks ----------

    window.TL_CUSTOM = {
        onBeforeInit({ context }, sdk) {
            try {
                state.rootUrl = context?.rootUrl || "/";
                if (String(context?.template || "").startsWith("collection")) injectStyle("tl-custom-cart-mode-style", CART_MODE_CSS);
                injectStyle("tl-custom-fb-style", FB_CSS);
                placeVolumeDiscountBelowAddToCart();
                patchTierRibbons();
                document.addEventListener("shopify:section:load", (event) => renderBanners(sdk, event.target));

                const monitor = sdk.CartMonitor;
                const original = monitor && monitor.reconcile;
                if (typeof original !== "function" || original.__tlPatched) return;

                let busy = false;
                let pending = null;

                // Tag plain lines with bundle properties; returns the latest cart and whether it changed.
                const adoptLines = async (cart, campaigns) => {
                    let changed = false;
                    for (const campaign of campaigns.filter(isAdoptable)) {
                        try {
                            const plan = planCampaign(cart, campaign, await matcherFor(campaign));
                            if (!plan) continue;
                            // Add before update: if add fails (e.g. out of stock), nothing is removed.
                            if (plan.additions.length) await sdk.ShopifyAPI.addItems(plan.additions);
                            if (Object.keys(plan.updates).length) await sdk.ShopifyAPI.updateCart(plan.updates);
                            changed = true;
                            cart = await sdk.ShopifyAPI.getCart();
                        } catch (error) {
                            console.error("[custom] adopt cart lines failed:", campaign.id, error);
                        }
                    }
                    return { cart, changed };
                };

                const patched = async function (cart, campaigns) {
                    campaigns = campaigns || [];
                    // The app passes every active campaign here, so banners need no extra request.
                    if (!state.campaigns) {
                        state.campaigns = campaigns;
                        renderBanners(sdk);
                    }
                    if (busy) {
                        pending = { campaigns };
                        return;
                    }
                    busy = true;

                    try {
                        if (campaigns.some(isAdoptable)) {
                            // Always plan from a fresh cart: the cart passed in can be another app's late
                            // response (cart drawer) that still lists lines we already bundled.
                            const result = await adoptLines(await sdk.ShopifyAPI.getCart(), campaigns);
                            await original.call(this, result.cart, campaigns);
                            if (result.changed) await sdk.ThemeAdapter.refreshCart();
                            await syncCartMode(sdk);
                        } else {
                            await original.call(this, cart, campaigns);
                        }
                    } catch (error) {
                        console.error("[custom] reconcile failed:", error);
                    } finally {
                        busy = false;
                    }

                    // Cart changed while running: run once more with the latest cart.
                    if (pending) {
                        const next = pending;
                        pending = null;
                        await patched.call(this, null, next.campaigns);
                    }
                };

                patched.__tlPatched = true;
                monitor.reconcile = patched;
            } catch (error) {
                console.error("[custom] onBeforeInit failed:", error);
            }
        },

        async onAfterInit({ context, widget }, sdk) {
            try {
                if (widget.tagName !== "TL-MIXNMATCH" || !isCartMode(context, widget.campaign)) return;
                enableCartMode(widget);
                state.cartModeWidgets.add(widget);
                await syncCartMode(sdk);
            } catch (error) {
                console.error("[custom] onAfterInit failed:", error);
            }
        },
    };
})();
